import React, { useState, useEffect } from 'react';
import { servicesCardData as defaultServicesData } from '../../utils/servicesCardData';
import '../../styles/AdminPages.css';

const AdminServices = () => {
  const [servicesData, setServicesData] = useState(defaultServicesData);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddArtistModal, setShowAddArtistModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null); // Track which artist is being edited
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newArtist, setNewArtist] = useState({
    name: '',
    title: '',
    location: '',
    experience: '',
    email: '',
    phone: '',
    bio: '',
    specialties: ''
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState('');
  const [portfolioPhotos, setPortfolioPhotos] = useState([]);
  const [portfolioPreviews, setPortfolioPreviews] = useState([]);

  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Load services data from localStorage on mount and merge with default
  useEffect(() => {
    const savedServices = localStorage.getItem('adminServicesData');
    const deletedArtists = JSON.parse(localStorage.getItem('deletedArtists') || '{}');
    let mergedData = {};
    
    // Start with default data - deep copy to avoid mutations
    // Filter out deleted artists from default data
    Object.keys(defaultServicesData).forEach(category => {
      const deletedIds = new Set(deletedArtists[category] || []);
      mergedData[category] = (defaultServicesData[category] || []).filter(
        artist => !deletedIds.has(artist.id)
      );
    });
    
    // Add admin-added artists
    if (savedServices) {
      try {
        const parsed = JSON.parse(savedServices);
        Object.keys(parsed).forEach(category => {
          const deletedIds = new Set(deletedArtists[category] || []);
          if (mergedData[category]) {
            // Append admin artists to default artists (avoid duplicates and deleted ones)
            const existingIds = new Set(mergedData[category].map(a => a.id));
            const adminArtists = (parsed[category] || []).filter(
              a => !existingIds.has(a.id) && !deletedIds.has(a.id)
            );
            mergedData[category] = [...mergedData[category], ...adminArtists];
          } else {
            // New category entirely from admin (filter deleted)
            mergedData[category] = (parsed[category] || []).filter(
              a => !deletedIds.has(a.id)
            );
          }
        });
      } catch (error) {
        console.error('Error loading services data:', error);
      }
    }
    
    setServicesData(mergedData);
    setIsInitialLoad(false);
  }, []);

  // Save only admin-added data to localStorage (not default data)
  useEffect(() => {
    // Skip saving on initial load
    if (isInitialLoad) return;
    if (!servicesData || Object.keys(servicesData).length === 0) return;
    
    // Extract only admin-added artists (those not in default data)
    const adminData = {};
    const defaultIdsByCategory = {};
    
    // Pre-compute default IDs for each category
    Object.keys(defaultServicesData).forEach(category => {
      defaultIdsByCategory[category] = new Set((defaultServicesData[category] || []).map(a => a.id));
    });
    
    Object.keys(servicesData).forEach(category => {
      if (defaultIdsByCategory[category]) {
        // Category exists in default - filter out default artists
        const adminArtists = (servicesData[category] || []).filter(
          artist => !defaultIdsByCategory[category].has(artist.id)
        );
        if (adminArtists.length > 0) {
          adminData[category] = adminArtists;
        }
      } else {
        // Entirely new category from admin
        adminData[category] = servicesData[category] || [];
      }
    });
    
    localStorage.setItem('adminServicesData', JSON.stringify(adminData));
    // Dispatch custom event for same-window updates
    window.dispatchEvent(new Event('adminServicesUpdated'));
  }, [servicesData, isInitialLoad]);

  const handleAddCategory = () => {
    if (newCategoryName.trim() && !servicesData[newCategoryName.trim()]) {
      setServicesData(prev => ({
        ...prev,
        [newCategoryName.trim()]: []
      }));
      setNewCategoryName('');
      setShowAddCategoryModal(false);
    }
  };

  const handleDeleteCategory = (categoryName) => {
    if (window.confirm(`Are you sure you want to delete the category "${categoryName}"? This will also delete all artists in this category.`)) {
      // If it's a default category, mark all default artists as deleted
      if (defaultServicesData[categoryName]) {
        const deletedArtists = JSON.parse(localStorage.getItem('deletedArtists') || '{}');
        const defaultIds = (defaultServicesData[categoryName] || []).map(a => a.id);
        deletedArtists[categoryName] = defaultIds;
        localStorage.setItem('deletedArtists', JSON.stringify(deletedArtists));
      }
      
      // Remove category from state
      const updated = { ...servicesData };
      delete updated[categoryName];
      setServicesData(updated);
      
      if (selectedCategory === categoryName) {
        setSelectedCategory(null);
      }
      
      // Dispatch event to update user pages immediately
      window.dispatchEvent(new Event('adminServicesUpdated'));
    }
  };

  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Profile photo must be less than 5MB');
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePortfolioPhotosChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 5) {
      alert('Maximum 5 portfolio images allowed');
      return;
    }
    
    const newPortfolio = [...portfolioPhotos, ...files].slice(0, 5);
    setPortfolioPhotos(newPortfolio);

    // Create previews
    const newPreviews = [];
    newPortfolio.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        if (newPreviews.length === newPortfolio.length) {
          setPortfolioPreviews(newPreviews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const removePortfolioPhoto = (index) => {
    setPortfolioPhotos(prev => prev.filter((_, i) => i !== index));
    setPortfolioPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Function to load artist data for editing
  const handleEditArtist = (artist) => {
    setEditingArtist(artist);
    setNewArtist({
      name: artist.name || '',
      title: artist.title || '',
      location: artist.location || '',
      experience: artist.experience || '',
      email: artist.email || '',
      phone: artist.phone || '',
      bio: artist.bio || '',
      specialties: artist.specialties ? (Array.isArray(artist.specialties) ? artist.specialties.join(', ') : artist.specialties) : ''
    });
    // Set existing images as previews
    if (artist.image) {
      setProfilePhotoPreview(artist.image);
    }
    if (artist.portfolio && Array.isArray(artist.portfolio) && artist.portfolio.length > 0) {
      setPortfolioPreviews(artist.portfolio);
    }
    setProfilePhoto(null); // Clear file input, user can upload new if needed
    setPortfolioPhotos([]);
    setShowAddArtistModal(true);
  };

  const handleAddArtist = async () => {
    if (!selectedCategory || !newArtist.name.trim()) return;

    const specialties = newArtist.specialties
      .split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    // Convert profile photo to base64
    let profileImage = '';
    if (profilePhoto) {
      // New photo uploaded
      profileImage = await fileToBase64(profilePhoto);
    } else if (editingArtist && editingArtist.image) {
      // Editing and keeping existing photo
      profileImage = editingArtist.image;
    } else {
      // Default placeholder
      profileImage = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&auto=format&q=80';
    }

    // Convert portfolio photos to base64
    const portfolio = [];
    // If editing, start with existing portfolio
    if (editingArtist && editingArtist.portfolio && Array.isArray(editingArtist.portfolio)) {
      portfolio.push(...editingArtist.portfolio);
    }
    // Add new portfolio photos
    if (portfolioPhotos.length > 0) {
      for (const file of portfolioPhotos) {
        const base64 = await fileToBase64(file);
        portfolio.push(base64);
      }
    }
    // Limit to 5 portfolio images
    const finalPortfolio = portfolio.slice(0, 5);
    // If no portfolio images at all (not editing and no new ones), add placeholder
    if (finalPortfolio.length === 0) {
      finalPortfolio.push('https://images.unsplash.com/photo-1553413077-190dd305871c?w=800&h=600&fit=crop&auto=format&q=80');
    }

    // Use existing artist ID if editing, otherwise create new one
    const artistId = editingArtist ? editingArtist.id : `${selectedCategory.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;

    const artist = {
      id: artistId,
      name: newArtist.name.trim(),
      title: newArtist.title.trim(),
      image: profileImage,
      location: newArtist.location.trim(),
      experience: newArtist.experience.trim(),
      portfolio: finalPortfolio,
      specialties: specialties.length > 0 ? specialties : ['General'],
      email: newArtist.email.trim(),
      phone: newArtist.phone.trim(),
      bio: newArtist.bio.trim()
    };

    setServicesData(prev => {
      if (!prev[selectedCategory]) {
        prev[selectedCategory] = [];
      }
      const currentArtists = prev[selectedCategory] || [];
      
      // If editing, update existing artist; otherwise add new
      if (editingArtist) {
        const updatedArtists = currentArtists.map(a => 
          a.id === editingArtist.id ? artist : a
        );
        return {
          ...prev,
          [selectedCategory]: updatedArtists
        };
      } else {
        return {
          ...prev,
          [selectedCategory]: [...currentArtists, artist]
        };
      }
    });

    // Reset form
    setNewArtist({
      name: '',
      title: '',
      location: '',
      experience: '',
      email: '',
      phone: '',
      bio: '',
      specialties: ''
    });
    setProfilePhoto(null);
    setProfilePhotoPreview('');
    setPortfolioPhotos([]);
    setPortfolioPreviews([]);
    setEditingArtist(null);
    setShowAddArtistModal(false);
    
    // Dispatch event to update user pages immediately
    window.dispatchEvent(new Event('adminServicesUpdated'));
  };

  const handleDeleteArtist = (e, categoryName, artistId) => {
    e.stopPropagation(); // Prevent any event bubbling
    if (window.confirm('Are you sure you want to delete this artist?')) {
      // Check if this is a default artist (from defaultServicesData)
      const isDefaultArtist = defaultServicesData[categoryName]?.some(
        artist => artist.id === artistId
      );
      
      if (isDefaultArtist) {
        // Track deleted default artist IDs in localStorage
        const deletedArtists = JSON.parse(localStorage.getItem('deletedArtists') || '{}');
        if (!deletedArtists[categoryName]) {
          deletedArtists[categoryName] = [];
        }
        if (!deletedArtists[categoryName].includes(artistId)) {
          deletedArtists[categoryName].push(artistId);
          localStorage.setItem('deletedArtists', JSON.stringify(deletedArtists));
        }
      }
      
      // Remove from state (works for both default and admin-added artists)
      setServicesData(prev => {
        if (!prev[categoryName]) return prev;
        
        const updatedArtists = prev[categoryName].filter(artist => artist.id !== artistId);
        
        return {
          ...prev,
          [categoryName]: updatedArtists
        };
      });
      
      // Dispatch event to update user pages immediately
      window.dispatchEvent(new Event('adminServicesUpdated'));
    }
  };

  // Use servicesData directly for display (already merged)
  const categories = Object.keys(servicesData);
  const currentArtists = selectedCategory ? (servicesData[selectedCategory] || []) : [];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Services Management</h1>
        <p>Manage service categories and artists</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem' }}>
        {/* Categories Sidebar */}
        <div className="admin-card">
          <div className="admin-table-header" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '1rem' }}>
            <button 
              className="admin-button primary" 
              onClick={() => setShowAddCategoryModal(true)}
            >
              + Add Category
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem' }}>
            {categories.map(category => (
              <div
                key={category}
                onClick={() => setSelectedCategory(category)}
                style={{
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  background: selectedCategory === category ? '#3b82f6' : '#f9fafb',
                  color: selectedCategory === category ? 'white' : '#374151',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                <span style={{ fontWeight: selectedCategory === category ? '600' : '500' }}>
                  {category}
                </span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                    ({servicesData[category]?.length || 0})
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteCategory(category);
                    }}
                    className="admin-button danger"
                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Artists List */}
        <div className="admin-card">
          {selectedCategory ? (
            <>
              <div className="admin-table-header">
                <h3 style={{ margin: 0 }}>{selectedCategory} Artists</h3>
                <button 
                  className="admin-button primary" 
                  onClick={() => setShowAddArtistModal(true)}
                >
                  + Add Artist
                </button>
              </div>

              <div className="admin-table-container" style={{ marginTop: '1.5rem' }}>
                {currentArtists.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Profile Photo</th>
                        <th>Name</th>
                        <th>Title</th>
                        <th>Location</th>
                        <th>Phone</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentArtists.map(artist => (
                        <tr 
                          key={artist.id}
                          onClick={() => handleEditArtist(artist)}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <td>
                            <img 
                              src={artist.image} 
                              alt={artist.name}
                              style={{ width: '50px', height: '50px', borderRadius: '50%', objectFit: 'cover', cursor: 'pointer' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditArtist(artist);
                              }}
                              onError={(e) => {
                                e.target.src = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&auto=format&q=80';
                              }}
                            />
                          </td>
                          <td>{artist.name}</td>
                          <td>{artist.title}</td>
                          <td>{artist.location}</td>
                          <td>{artist.phone || 'N/A'}</td>
                          <td>
                            <button
                              className="admin-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditArtist(artist);
                              }}
                              style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', marginRight: '0.5rem' }}
                            >
                              Edit
                            </button>
                            <button
                              className="admin-button danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteArtist(e, selectedCategory, artist.id);
                              }}
                              style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                    No artists in this category. Click "Add Artist" to add one.
                  </p>
                )}
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
              <p>Select a category from the sidebar to view and manage artists.</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div className="admin-card" style={{ maxWidth: '500px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginTop: 0 }}>Add New Category</h2>
            <div className="admin-form">
              <div className="admin-form-group">
                <label>Category Name</label>
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="e.g., Photography Services"
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button 
                  className="admin-button secondary"
                  onClick={() => {
                    setShowAddCategoryModal(false);
                    setNewCategoryName('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="admin-button primary"
                  onClick={handleAddCategory}
                  disabled={!newCategoryName.trim()}
                >
                  Add Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Artist Modal */}
      {showAddArtistModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div className="admin-card" style={{ maxWidth: '700px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginTop: 0 }}>{editingArtist ? 'Edit Artist' : `Add Artist to ${selectedCategory}`}</h2>
            <div className="admin-form">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="admin-form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newArtist.name}
                    onChange={(e) => setNewArtist({ ...newArtist, name: e.target.value })}
                    placeholder="Artist name"
                    required
                  />
                </div>
                <div className="admin-form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={newArtist.title}
                    onChange={(e) => setNewArtist({ ...newArtist, title: e.target.value })}
                    placeholder="Professional Photographer"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="admin-form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={newArtist.location}
                    onChange={(e) => setNewArtist({ ...newArtist, location: e.target.value })}
                    placeholder="New York, NY"
                  />
                </div>
                <div className="admin-form-group">
                  <label>Experience</label>
                  <input
                    type="text"
                    value={newArtist.experience}
                    onChange={(e) => setNewArtist({ ...newArtist, experience: e.target.value })}
                    placeholder="10+ Years"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="admin-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={newArtist.email}
                    onChange={(e) => setNewArtist({ ...newArtist, email: e.target.value })}
                    placeholder="artist@example.com"
                  />
                </div>
                <div className="admin-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newArtist.phone}
                    onChange={(e) => setNewArtist({ ...newArtist, phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div className="admin-form-group">
                <label>Profile Photo {!editingArtist ? '*' : ''} (Single Image, Max 5MB)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoChange}
                  style={{ padding: '0.5rem' }}
                />
                {profilePhotoPreview && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <img 
                      src={profilePhotoPreview} 
                      alt="Profile preview"
                      style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #e5e7eb', cursor: 'pointer' }}
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.onchange = (e) => handleProfilePhotoChange(e);
                        input.click();
                      }}
                      title="Click to change photo"
                    />
                    {editingArtist && (
                      <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
                        Click image to change or leave blank to keep current
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="admin-form-group">
                <label>Portfolio Photos (you can upload Max 5 of you best work,Max 5MB each)</label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePortfolioPhotosChange}
                  style={{ padding: '0.5rem' }}
                  disabled={portfolioPhotos.length >= 5}
                />
                {portfolioPreviews.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {portfolioPreviews.map((preview, index) => (
                      <div key={index} style={{ position: 'relative' }}>
                        <img 
                          src={preview} 
                          alt={`Portfolio ${index + 1}`}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '0.5rem', border: '2px solid #e5e7eb' }}
                        />
                        <button
                          type="button"
                          onClick={() => removePortfolioPhoto(index)}
                          style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            background: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {portfolioPhotos.length >= 5 && (
                  <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                    Maximum 5 portfolio images reached
                  </p>
                )}
              </div>

              <div className="admin-form-group">
                <label>Specialties (comma separated)</label>
                <input
                  type="text"
                  value={newArtist.specialties}
                  onChange={(e) => setNewArtist({ ...newArtist, specialties: e.target.value })}
                  placeholder="Portrait, Fashion, Commercial"
                />
              </div>

              <div className="admin-form-group">
                <label>Bio</label>
                <textarea
                  rows="4"
                  value={newArtist.bio}
                  onChange={(e) => setNewArtist({ ...newArtist, bio: e.target.value })}
                  placeholder="Artist bio description..."
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button 
                  className="admin-button secondary"
                  onClick={() => {
                    setShowAddArtistModal(false);
                    setNewArtist({
                      name: '',
                      title: '',
                      location: '',
                      experience: '',
                      email: '',
                      phone: '',
                      bio: '',
                      specialties: ''
                    });
                    setProfilePhoto(null);
                    setProfilePhotoPreview('');
                    setPortfolioPhotos([]);
                    setPortfolioPreviews([]);
                    setEditingArtist(null);
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="admin-button primary"
                  onClick={handleAddArtist}
                  disabled={!newArtist.name.trim() || (!profilePhoto && !editingArtist)}
                >
                  {editingArtist ? 'Update Artist' : 'Add Artist'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;

